import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  CarbonStackedLineChart,
  TransitionsEmissionsChart,
  SettlementDestinationEmissionsChart,
  Emissions_Lost_Forest,
  ParallelChart
} from "../charts/charts";
import "./SectionThree.css";
import CollapsibleCitation from "../tools/CollapsibleCitation";
import InformationPopUp from "../tools/InformationPopUp";

export default function EmisionesCarbono() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.5, once: false });

  // helper for staggering
  const animateProps = (delay = 0) => ({
    initial: { opacity: 0, y: 10 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay }
  });

  return (
    <section
      ref={sectionRef}
      className="h-screen snap-start bg-gradient-to-b from-[#324130] to-[#92843B] text-white"
    >
      <div className="h-full p-6" style={{ marginTop: "20vh" }}>
        {/* <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-4xl font-bold mb-6"
        >
          EMISIONES DE CARBONO
        </motion.h2>
 */}
        {/* Two-column layout */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-2" style={{ marginTop: "5vh" }}>
          {/* LEFT column: two line charts */}
          <div className="flex flex-col h-screen p-6" >
            {/* Urbanization emission line */}
            <motion.div {...animateProps(0.2)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-2xl font-semibold mb-2"
              >
                CO₂ emissions caused by new urbanized land
                <InformationPopUp title="CO₂ emissions caused by new urbanized land"
                description="This chart shows the total carbon dioxide emitted per year when previously undeveloped land is urbanized."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Global Land Cover"
                  source="Own elaboration based on SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ height: "35vh", maxHeight: "35vh" }}>
                <SettlementDestinationEmissionsChart />
              </div>
            </motion.div>

            {/* Forest-loss emission line */}
            <motion.div {...animateProps(0.6)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-2xl font-semibold mb-2"
              >
                CO₂ emissions caused by forest area loss
                <InformationPopUp title="CO₂ emissions caused by forest area loss"
                description="Similar to the previous chart, this one shows the CO2 emitted when forests (both primary and secondary) are converted to any other land type."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Global Land Cover"
                  source="Own elaboration based on SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ height: "35vh", maxHeight: "35vh" }}>
                <Emissions_Lost_Forest />
              </div>
              
              
            </motion.div>
          </div>
          {/* New RIGHT column: emissions stacked bar chart */}

          <div className="flex flex-col h-screen p-6" >
            {/* Línea de emisiones por urbanización */}
            <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-2xl font-semibold mb-2"
              >
              
              Balanced carbon capture and emissions
              <InformationPopUp title="Balanced carbon capture and emissions"
                description="The CO2 emissions are produced by <b>four</b> different mechanisms: <br />
<ol>
<li><b>1. Forests</b>: Due to their high vegetation content, forests capture a large amount of carbon from the atmosphere. </li>
<br />    
<li><b>2. Land use transitions</b>: All land types have a certain amount of associated biomass; in simple terms, this is the total mass of all organisms living within it. For example, a <b>forest</b> has a very high biomass, while that of <b>urbanized land</b> is low.
All living organisms store a certain amount of carbon within them, which is released into the atmosphere when they die and decay. If an area with a large amount of biomass transitions to a land type with lower biomass (for example, if a forest becomes a pasture), a certain amount of carbon will be emitted proportional to the difference in biomass between both land types, and the total area.
This process can also occur in reverse: if a low-biomass area becomes one with high biomass, carbon will be absorbed from the atmosphere.</li>
<br />
<li><b>3. Soil mineralization and organic soils</b>: Biomass is not only found in animals and plants; the soil itself is teeming with microorganisms and decomposing organic matter. When an area loses biomass (for example, if a forest is cut down), the soil is also affected, becoming arid and dry. This process is known as mineralization.</li>
<br />
<li><b>4. N2O equivalents</b>: The previous two points estimate emissions produced in tons of carbon dioxide (CO2). However, this is not the only greenhouse gas produced by these processes; another of great importance is nitrous oxide (N2O). According to estimates made by the IPCC, 1 ton of N2O is equivalent to 280 tons of CO2. The SISEPUEDE model estimates N2O emissions and reports its contribution using this equivalence.</li>
</ol>
This chart shows the carbon absorbed and emitted through each of these mechanisms, and the total result.
"
bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Global Land Cover"
                  source="Own elaboration based on SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ height: "35vh", maxHeight: "35vh" }}>
                <CarbonStackedLineChart />
              </div>
            </motion.div>

            <motion.div {...animateProps(1.0)}></motion.div>
            <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-2xl font-semibold mb-2"
              >
                CO₂ capture potential lost and urbanized area
                <InformationPopUp title="CO₂ capture potential lost and urbanized area"
                description="

                From the <b>11 land use categories</b> used in the model, <b>3 of them</b> are human-made:
                <ul>
                <li><b>·Croplands</b></li>
                <li><b>·Pastures</b></li>
                <li><b>·Settlements</b></li>  
                </ul>
                This chart shows the CO₂ emitted when an area is converted to one of these three classes
                "
                  bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="GLC_FCS30D: Global Land Cover"
                  source=" Own elaboration based on SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ height: "35vh", maxHeight: "35vh" }}>
                <ParallelChart />
              </div>

                            
            </motion.div>
          </div>
          

          {/* Old RIGHT column: stacked bar chart */}

          
          {/* <div className="flex flex-col flex-1 gap-8">
            <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-2xl font-semibold mb-2"
              >
                Evolución de emisiones de CO₂
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4" style={{ minHeight: "70vh" }}>
                <CarbonStackedLineChart />
              </div>
            </motion.div>
          </div> */}
        </div>
      </div>
    </section>
  );
}